import { FocusEvent, useEffect, useRef } from 'react';
import { useTranslation } from 'react-i18next';
import { toast, ToastContainer } from 'react-toastify';
import { findTabbableAfter } from '../../helpers/tabbableHelper';

const TOAST_SELECTOR = '.Toastify__toast';

// Removal is reported before the toast leaves the DOM, so focus may still sit inside it.
const focusLeavesWith = (toastId: string) => {
  const active = document.activeElement;
  return !active || active === document.body || active.closest(TOAST_SELECTOR)?.id === toastId;
};

const dismissFocusedToastOnEscape = (event: KeyboardEvent) => {
  if (event.key !== 'Escape' || !(event.target instanceof Element)) return;
  const toastElement = event.target.closest<HTMLElement>(TOAST_SELECTOR);
  if (!toastElement?.id) return;
  event.preventDefault();
  toast.dismiss(toastElement.id);
};

/** Toasts never take focus themselves; Alt+T reaches them, Escape dismisses one, and focus then goes back. */
const ToastRegion = () => {
  const { t } = useTranslation();
  const regionRef = useRef<HTMLDivElement>(null);
  const returnFocusRef = useRef<HTMLElement | null>(null);
  const focusWasInToastsRef = useRef(false);

  useEffect(() => {
    const region = regionRef.current;
    region?.addEventListener('keydown', dismissFocusedToastOnEscape);
    return () => region?.removeEventListener('keydown', dismissFocusedToastOnEscape);
  }, []);

  useEffect(
    () =>
      toast.onChange(({ status, id }) => {
        if (status !== 'removed' || !focusWasInToastsRef.current) return;
        if (!focusLeavesWith(String(id))) return;
        focusWasInToastsRef.current = false;
        const returnTo = returnFocusRef.current;
        const target = returnTo?.isConnected
          ? returnTo
          : regionRef.current && findTabbableAfter(regionRef.current);
        target?.focus();
      }),
    [],
  );

  const rememberWhereFocusCameFrom = (event: FocusEvent<HTMLDivElement>) => {
    focusWasInToastsRef.current = true;
    const from = event.relatedTarget;
    if (from instanceof Node && event.currentTarget.contains(from)) return;
    returnFocusRef.current = from instanceof HTMLElement && from !== document.body ? from : null;
  };

  // A focused toast that gets removed blurs with no relatedTarget, which is not the user leaving.
  const noteFocusLeaving = (event: FocusEvent<HTMLDivElement>) => {
    const to = event.relatedTarget;
    if (to instanceof Node && !event.currentTarget.contains(to))
      focusWasInToastsRef.current = false;
  };

  return (
    <div
      ref={regionRef}
      onFocus={rememberWhereFocusCameFrom}
      onBlur={noteFocusLeaving}
      data-testid="toast-region"
    >
      <ToastContainer aria-label={`${t('common:notifications')} (Alt+T)`} />
    </div>
  );
};

export default ToastRegion;
